import { useState, useEffect, useCallback, useRef } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Calendar as CalIcon, Plus, Clock, QrCode, Copy, ExternalLink, CheckCircle, Edit, Trash2, MessageCircle, Loader2, UserCheck, UserX, LogIn, LogOut, Bell, Phone, User } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { QRCodeSVG } from 'qrcode.react';

const FRONTEND_URL = window.location.origin;

export default function AppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [rejectingAppointment, setRejectingAppointment] = useState(null);
  const [clinicSettings, setClinicSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({ patient_id: '', patient_name: '', time_slot: '', type: 'scheduled', notes: '' });
  const [showQuickAddPatient, setShowQuickAddPatient] = useState(false);
  const [quickPatient, setQuickPatient] = useState({ name: '', phone: '', age: '', gender: '' });
  const [creatingPatient, setCreatingPatient] = useState(false);
  const [editForm, setEditForm] = useState({ time_slot: '', date: '', notes: '' });
  const [rejectForm, setRejectForm] = useState({ reason: '', suggested_dates: [], suggested_times: [] });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [activeTab, setActiveTab] = useState('appointments');

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  const fetchAppointments = async () => {
    try {
      const res = await API.get(`/appointments?date_filter=${dateStr}`);
      setAppointments(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  
  const fetchPendingRequests = async () => {
    try {
      const res = await API.get('/appointments/pending-requests');
      setPendingRequests(res.data);
    } catch (err) { console.error(err); }
  };
  
  const fetchAvailableSlots = async (date) => {
    try {
      const res = await API.get(`/appointments/doctor-availability?date=${date}`);
      setAvailableSlots(res.data.available_slots || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    fetchAppointments();
    fetchPendingRequests();
    Promise.all([
      API.get('/patients'),
      API.get('/clinic-settings')
    ]).then(([p, cs]) => {
      setPatients(p.data);
      setClinicSettings(cs.data);
    }).catch(console.error);
  }, [dateStr]);

  // Auto-refresh appointments every 20s
  const pollRef = useRef(null);
  useEffect(() => {
    pollRef.current = setInterval(() => {
      if (!document.hidden) { fetchAppointments(); fetchPendingRequests(); }
    }, 20000);
    const onVis = () => { if (!document.hidden) { fetchAppointments(); fetchPendingRequests(); } };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(pollRef.current); document.removeEventListener('visibilitychange', onVis); };
  }, [dateStr]);
  
  // Appointment Actions
  const handleApprove = async (appointmentId, timeSlot = null) => {
    try {
      await API.put(`/appointments/${appointmentId}/approve${timeSlot ? `?time_slot=${timeSlot}` : ''}`);
      toast.success('Appointment approved');
      fetchAppointments();
      fetchPendingRequests();
    } catch (err) { 
      toast.error('Failed to approve appointment');
    }
  };
  
  const handleReject = async () => {
    if (!rejectingAppointment) return;
    try {
      await API.put(`/appointments/${rejectingAppointment.id}/reject`, rejectForm);
      toast.success('Appointment rejected with suggestions sent to patient');
      setRejectDialogOpen(false);
      setRejectingAppointment(null);
      setRejectForm({ reason: '', suggested_dates: [], suggested_times: [] });
      fetchPendingRequests();
    } catch (err) {
      toast.error('Failed to reject appointment');
    }
  };
  
  const handleCheckIn = async (appointmentId) => {
    try {
      await API.put(`/appointments/${appointmentId}/check-in`);
      toast.success('Patient checked in');
      fetchAppointments();
    } catch (err) {
      toast.error('Failed to check in patient');
    }
  };
  
  const handleCheckOut = async (appointmentId) => {
    try {
      await API.put(`/appointments/${appointmentId}/check-out`);
      toast.success('Appointment completed');
      fetchAppointments();
    } catch (err) {
      toast.error('Failed to complete appointment');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await API.post('/appointments', { ...form, date: dateStr });
      toast.success('Appointment created');
      setDialogOpen(false);
      setForm({ patient_id: '', patient_name: '', time_slot: '', type: 'scheduled', notes: '' });
      fetchAppointments();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to create');
    }
  };

  const handleQuickAddPatient = async () => {
    if (!quickPatient.name || !quickPatient.phone) { toast.error('Name and phone are required'); return; }
    setCreatingPatient(true);
    try {
      const res = await API.post('/patients', {
        name: quickPatient.name, phone: quickPatient.phone,
        age: quickPatient.age ? parseInt(quickPatient.age) : 0,
        gender: quickPatient.gender || ''
      });
      const newPatient = res.data;
      setPatients(prev => [newPatient, ...prev]);
      setForm({ ...form, patient_id: newPatient.id, patient_name: newPatient.name });
      setShowQuickAddPatient(false);
      setQuickPatient({ name: '', phone: '', age: '', gender: '' });
      toast.success(`Patient "${newPatient.name}" added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add patient');
    } finally {
      setCreatingPatient(false);
    }
  };

  const handleStatus = async (id, status) => {
    try {
      await API.put(`/appointments/${id}`, { status });
      fetchAppointments();
      toast.success(`Status updated to ${status}`);
    } catch (err) { toast.error('Update failed'); }
  };

  const openEditDialog = (appt) => {
    setEditingAppointment(appt);
    setEditForm({
      time_slot: appt.time_slot || '',
      date: appt.date || dateStr,
      notes: appt.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingAppointment) return;
    try {
      await API.put(`/appointments/${editingAppointment.id}`, editForm);
      toast.success('Appointment updated');
      setEditDialogOpen(false);
      setEditingAppointment(null);
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleDelete = async (appt) => {
    setDeleting(true);
    try {
      await API.delete(`/appointments/${appt.id}`);
      toast.success('Appointment deleted');
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const openQrDialog = (appt) => {
    setSelectedAppointment(appt);
    setQrDialogOpen(true);
  };

  const copyKioskLink = (token) => {
    const link = `${FRONTEND_URL}/kiosk/${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Kiosk link copied to clipboard');
  };

  const getKioskUrl = (token) => `${FRONTEND_URL}/kiosk/${token}`;

  // Send WhatsApp reminder
  const sendWhatsAppReminder = (appt) => {
    const patient = patients.find(p => p.id === appt.patient_id);
    const clinicName = clinicSettings?.clinic_name || '1Clin';
    const clinicPhone = clinicSettings?.clinic_phone || '';
    
    let message = `*${clinicName} - Appointment Reminder*\n\n`;
    message += `Dear ${appt.patient_name},\n\n`;
    message += `This is a reminder for your upcoming appointment:\n\n`;
    message += `*Date:* ${appt.date}\n`;
    if (appt.time_slot) message += `*Time:* ${appt.time_slot}\n`;
    message += `*Token:* #${appt.token_number}\n\n`;
    
    if (appt.notes) {
      message += `*Notes:* ${appt.notes}\n\n`;
    }
    
    // Include kiosk check-in link
    message += `*Self Check-In:* You can check-in before arrival using this link:\n`;
    message += `${FRONTEND_URL}/kiosk/${appt.kiosk_token}\n\n`;
    
    if (clinicPhone) {
      message += `For queries, contact: ${clinicPhone}\n\n`;
    }
    
    message += `_Thank you for choosing ${clinicName}_`;
    
    const phoneNumber = patient?.phone?.replace(/[^0-9]/g, '') || '';
    const whatsappUrl = phoneNumber 
      ? `https://wa.me/91${phoneNumber}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp...');
  };

  const statusColors = {
    waiting: 'bg-amber-100 text-amber-800',
    'in-progress': 'bg-blue-100 text-blue-800',
    completed: 'bg-emerald-100 text-emerald-800',
    cancelled: 'bg-slate-100 text-slate-600',
    'no-show': 'bg-red-50 text-red-600',
  };

  const timeSlots = ['09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM', '06:00 PM'];

  return (
    <div data-testid="appointments-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{fontFamily:'Manrope'}}>Appointments</h1>
          <p className="text-sm text-slate-500">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white h-11" data-testid="appointments-add-btn">
              <Plus className="w-4 h-4 mr-2" /> New Appointment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{fontFamily:'Manrope'}}>New Appointment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Patient</Label>
                {!showQuickAddPatient ? (
                  <Select
                    value={form.patient_id}
                    onValueChange={(v) => {
                      if (v === '__new__') { setShowQuickAddPatient(true); return; }
                      const p = patients.find(p => p.id === v);
                      setForm({...form, patient_id: v, patient_name: p?.name || ''});
                    }}
                  >
                    <SelectTrigger className="h-12 mt-1" data-testid="appointment-patient-select">
                      <SelectValue placeholder="Select patient" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__new__" data-testid="appointment-add-new-patient-option" className="text-teal-600 font-medium">
                        + Add New Patient
                      </SelectItem>
                      {patients.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name} - {p.phone}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="mt-1 p-3 border border-teal-200 rounded-lg bg-teal-50 space-y-2" data-testid="quick-add-patient-form">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-teal-700">New Patient Details</p>
                      <button type="button" className="text-slate-400 hover:text-slate-600" onClick={() => setShowQuickAddPatient(false)}>
                        <span className="text-xs">Cancel</span>
                      </button>
                    </div>
                    <Input
                      placeholder="Full name *"
                      value={quickPatient.name}
                      onChange={e => setQuickPatient({ ...quickPatient, name: e.target.value })}
                      className="h-10 bg-white"
                      data-testid="quick-add-patient-name"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        placeholder="Phone *"
                        className="h-10 bg-white col-span-2"
                        value={quickPatient.phone}
                        onChange={e => setQuickPatient({ ...quickPatient, phone: e.target.value })}
                        data-testid="quick-add-patient-phone"
                      />
                      <Input
                        placeholder="Age"
                        type="number"
                        className="h-10 bg-white"
                        value={quickPatient.age}
                        onChange={e => setQuickPatient({ ...quickPatient, age: e.target.value })}
                        data-testid="quick-add-patient-age"
                      />
                    </div>
                    <Button type="button" size="sm" className="w-full bg-teal-700 hover:bg-teal-800"
                      onClick={handleQuickAddPatient} disabled={creatingPatient} data-testid="quick-add-patient-save-btn">
                      {creatingPatient ? 'Adding...' : 'Save & Select Patient'}
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <Label>Time Slot</Label>
                <Select value={form.time_slot} onValueChange={v => setForm({...form, time_slot: v})}>
                  <SelectTrigger className="h-12 mt-1" data-testid="appointment-time-select">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                  <SelectTrigger className="h-12 mt-1" data-testid="appointment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="walk-in">Walk-in</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Input
                  data-testid="appointment-notes-input"
                  value={form.notes}
                  onChange={e => setForm({...form, notes: e.target.value})}
                  placeholder="Optional notes"
                  className="h-12 mt-1"
                />
              </div>
              <Button type="submit" className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white" data-testid="appointment-submit-btn">
                Create Appointment
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Manrope'}}>Self Check-In QR Code</DialogTitle>
          </DialogHeader>
          {selectedAppointment && (
            <div className="text-center space-y-4">
              <div className="bg-white p-4 rounded-lg inline-block mx-auto">
                <QRCodeSVG 
                  value={getKioskUrl(selectedAppointment.kiosk_token)} 
                  size={180}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{selectedAppointment.patient_name}</p>
                <p className="text-sm text-slate-500">Token #{selectedAppointment.token_number}</p>
                {selectedAppointment.kiosk_checked_in && (
                  <Badge className="mt-2 bg-emerald-100 text-emerald-800">
                    <CheckCircle className="w-3 h-3 mr-1" /> Already Checked In
                  </Badge>
                )}
              </div>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" size="sm" onClick={() => copyKioskLink(selectedAppointment.kiosk_token)} data-testid="copy-kiosk-link">
                  <Copy className="w-4 h-4 mr-1" /> Copy Link
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.open(getKioskUrl(selectedAppointment.kiosk_token), '_blank')} data-testid="open-kiosk-link">
                  <ExternalLink className="w-4 h-4 mr-1" /> Open
                </Button>
              </div>
              <p className="text-xs text-slate-400">Patient can scan this QR code to check-in and enter their symptoms before the consultation.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Appointment Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Manrope'}}>Edit Appointment</DialogTitle>
          </DialogHeader>
          {editingAppointment && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <Label>Patient</Label>
                <Input value={editingAppointment.patient_name} disabled className="h-12 mt-1 bg-slate-50" />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={editForm.date}
                  onChange={e => setEditForm({...editForm, date: e.target.value})}
                  className="h-12 mt-1"
                  data-testid="edit-appointment-date"
                />
              </div>
              <div>
                <Label>Time Slot</Label>
                <Select value={editForm.time_slot} onValueChange={v => setEditForm({...editForm, time_slot: v})}>
                  <SelectTrigger className="h-12 mt-1" data-testid="edit-appointment-time-select">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Input
                  value={editForm.notes}
                  onChange={e => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Optional notes"
                  className="h-12 mt-1"
                  data-testid="edit-appointment-notes"
                />
              </div>
              <Button type="submit" className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white" data-testid="edit-appointment-submit-btn">
                Save Changes
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2 border-slate-200 dark:border-slate-800 overflow-hidden">
          <CardContent className="p-2 sm:p-3" data-testid="appointments-calendar">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              className="rounded-md"
            />
          </CardContent>
        </Card>

        {/* Appointment List with Tabs */}
        <Card className="md:col-span-1 lg:col-span-3 border-slate-200 dark:border-slate-800">
          <CardHeader className="pb-3">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="appointments" className="flex items-center gap-2">
                  <CalIcon className="w-4 h-4" /> Today ({appointments.length})
                </TabsTrigger>
                <TabsTrigger value="requests" className="flex items-center gap-2" onClick={fetchPendingRequests}>
                  <Bell className="w-4 h-4" /> Requests 
                  {pendingRequests.length > 0 && (
                    <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0">{pendingRequests.length}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              
              {/* Today's Appointments Tab */}
              <TabsContent value="appointments" className="mt-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-2">
                    {['waiting', 'checked-in', 'in-progress', 'completed'].map(s => {
                      const count = appointments.filter(a => a.status === s).length;
                      return count > 0 ? (
                        <Badge key={s} className={`text-[10px] ${statusColors[s] || 'bg-slate-100 text-slate-600'}`}>{s}: {count}</Badge>
                      ) : null;
                    })}
                  </div>
                </div>
                
                <div className="space-y-2" data-testid="appointments-list">
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="w-6 h-6 border-2 border-teal-700 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : appointments.length === 0 ? (
                    <div className="text-center py-12">
                      <CalIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-400 text-sm">No appointments for this date</p>
                    </div>
                  ) : (
                    appointments.map((appt, i) => (
                      <div
                        key={appt.id}
                        className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg border-l-4 bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 hover:shadow-sm transition-all ${
                          appt.status === 'waiting' ? 'border-l-amber-400' : 
                          appt.status === 'checked-in' ? 'border-l-purple-400' : 
                          appt.status === 'in-progress' ? 'border-l-blue-400' : 
                          appt.status === 'completed' ? 'border-l-emerald-400' : 
                          appt.status === 'confirmed' ? 'border-l-green-400' :
                          appt.status === 'requested' ? 'border-l-orange-400' :
                          'border-l-slate-300'
                        }`}
                        data-testid={`appointment-item-${i}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                            <span className="text-sm font-bold text-slate-600 dark:text-slate-300">#{appt.token_number || i+1}</span>
                          </div>
                          <div>
                            <p className="font-medium text-sm text-slate-900 dark:text-white">{appt.patient_name}</p>
                            <div className="flex items-center gap-3 mt-0.5">
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {appt.time_slot || appt.time || 'No time'}
                              </span>
                              <span className="text-xs text-slate-400 capitalize">{appt.type}</span>
                              {appt.source === 'patient_portal' && (
                                <Badge className="text-[9px] bg-purple-50 text-purple-700">Patient Portal</Badge>
                              )}
                              {appt.kiosk_checked_in && (
                                <Badge className="text-[9px] bg-emerald-50 text-emerald-700">Kiosk</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          <Badge className={`text-[10px] ${statusColors[appt.status] || 'bg-slate-100 text-slate-600'}`}>{appt.status}</Badge>
                          
                          {/* WhatsApp Reminder */}
                          <Button size="sm" variant="ghost" className="h-8 text-green-600 hover:bg-green-50" onClick={() => sendWhatsAppReminder(appt)} data-testid={`appt-whatsapp-${i}`} title="Send WhatsApp Reminder">
                            <MessageCircle className="w-4 h-4" />
                          </Button>
                          
                          {/* QR Code Button */}
                          <Button size="sm" variant="ghost" className="h-8 text-slate-500 hover:text-teal-600" onClick={() => openQrDialog(appt)} data-testid={`appt-qr-${i}`} title="Self Check-In QR">
                            <QrCode className="w-4 h-4" />
                          </Button>
                          
                          {/* Edit Button */}
                          <Button size="sm" variant="ghost" className="h-8 text-slate-500 hover:text-blue-600" onClick={() => openEditDialog(appt)} data-testid={`appt-edit-${i}`} title="Edit">
                            <Edit className="w-4 h-4" />
                          </Button>
                          
                          {/* Delete Button */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-8 text-slate-500 hover:text-red-600" data-testid={`appt-delete-${i}`} title="Delete">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Appointment?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete the appointment for <strong>{appt.patient_name}</strong> on {appt.date}.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  className="bg-red-600 hover:bg-red-700"
                                  onClick={() => handleDelete(appt)}
                                  disabled={deleting}
                                >
                                  {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          
                          {/* Status Action Buttons */}
                          {(appt.status === 'confirmed' || appt.status === 'waiting') && (
                            <Button size="sm" className="h-8 bg-purple-600 hover:bg-purple-700 text-white text-xs" onClick={() => handleCheckIn(appt.id)} data-testid={`appt-checkin-${i}`}>
                              <LogIn className="w-3 h-3 mr-1" /> Check-In
                            </Button>
                          )}
                          {appt.status === 'checked-in' && (
                            <>
                              <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-xs" onClick={() => handleStatus(appt.id, 'in-progress')} data-testid={`appt-start-${i}`}>Start</Button>
                            </>
                          )}
                          {appt.status === 'in-progress' && (
                            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => handleCheckOut(appt.id)} data-testid={`appt-checkout-${i}`}>
                              <LogOut className="w-3 h-3 mr-1" /> Check-Out
                            </Button>
                          )}
                          {appt.status === 'waiting' && !appt.source && (
                            <Button size="sm" variant="ghost" className="h-8 text-xs text-red-500" onClick={() => handleStatus(appt.id, 'no-show')} data-testid={`appt-noshow-${i}`}>No-Show</Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
              
              {/* Pending Requests Tab */}
              <TabsContent value="requests" className="mt-4">
                <div className="space-y-3">
                  {pendingRequests.length === 0 ? (
                    <div className="text-center py-12">
                      <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-400 text-sm">No pending appointment requests</p>
                      <p className="text-slate-300 text-xs mt-1">Requests from the Patient Portal will appear here</p>
                    </div>
                  ) : (
                    pendingRequests.map((req, i) => (
                      <div
                        key={req.id}
                        className="p-4 rounded-lg border-l-4 border-l-orange-400 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800"
                      >
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <User className="w-4 h-4 text-orange-600" />
                              <span className="font-semibold text-slate-800">{req.patient_name}</span>
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">Patient Portal</Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                              <div className="flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {req.patient_phone || 'N/A'}
                              </div>
                              <div className="flex items-center gap-1">
                                <CalIcon className="w-3 h-3" /> {req.date}
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {req.time || 'Flexible'}
                              </div>
                            </div>
                            {req.reason && (
                              <p className="text-sm text-slate-500 mt-2 bg-white p-2 rounded">
                                <strong>Reason:</strong> {req.reason}
                              </p>
                            )}
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex flex-col gap-2 min-w-[140px]">
                            <Button 
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleApprove(req.id, req.time)}
                            >
                              <UserCheck className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button 
                              variant="destructive"
                              onClick={() => {
                                setRejectingAppointment(req);
                                fetchAvailableSlots(req.date);
                                setRejectDialogOpen(true);
                              }}
                            >
                              <UserX className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardHeader>
        </Card>
      </div>
      
      {/* Reject Appointment Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Appointment Request</DialogTitle>
            <CardDescription>Optionally suggest alternative dates/times for the patient</CardDescription>
          </DialogHeader>
          {rejectingAppointment && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{rejectingAppointment.patient_name}</p>
                <p className="text-sm text-slate-500">Requested: {rejectingAppointment.date} at {rejectingAppointment.time || 'Flexible'}</p>
              </div>
              
              <div>
                <Label>Reason for Rejection (Optional)</Label>
                <Textarea
                  value={rejectForm.reason}
                  onChange={(e) => setRejectForm({ ...rejectForm, reason: e.target.value })}
                  placeholder="e.g., Fully booked on this date"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Suggest Alternative Dates (Optional)</Label>
                <Input
                  type="date"
                  className="mt-1"
                  onChange={(e) => {
                    if (e.target.value && !rejectForm.suggested_dates.includes(e.target.value)) {
                      setRejectForm({ 
                        ...rejectForm, 
                        suggested_dates: [...rejectForm.suggested_dates, e.target.value] 
                      });
                      fetchAvailableSlots(e.target.value);
                    }
                  }}
                />
                {rejectForm.suggested_dates.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {rejectForm.suggested_dates.map((d, i) => (
                      <Badge key={`badge-${i}`} variant="secondary" className="text-xs">
                        {d}
                        <button 
                          className="ml-1 text-red-500" 
                          onClick={() => setRejectForm({
                            ...rejectForm,
                            suggested_dates: rejectForm.suggested_dates.filter((_, idx) => idx !== i)
                          })}
                        >×</button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              {availableSlots.length > 0 && (
                <div>
                  <Label>Available Time Slots</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {availableSlots.map((slot) => (
                      <Badge 
                        key={slot}
                        variant={rejectForm.suggested_times.includes(slot) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => {
                          if (rejectForm.suggested_times.includes(slot)) {
                            setRejectForm({
                              ...rejectForm,
                              suggested_times: rejectForm.suggested_times.filter(s => s !== slot)
                            });
                          } else {
                            setRejectForm({
                              ...rejectForm,
                              suggested_times: [...rejectForm.suggested_times, slot]
                            });
                          }
                        }}
                      >
                        {slot}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject}>Reject & Notify Patient</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
